import { useCallback, useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet } from 'react-router';
import Icon from '../components/Icon';
import Avatar from '../components/Avatar';
import LoadingBar from '../components/LoadingBar';
import FeedbackButton from '../components/FeedbackButton';
import ModeSheet from './ModeSheet';
import { useApp, useAppPath } from '../lib/AppContext';
import { useRememberMode } from '../lib/mode';
import { useAnnouncementAnchor } from '../components/Announcements';

const TABS = [
  { to: '/coach', label: 'Học viên', icon: 'users', end: true },
  { to: '/coach/plan', label: 'Kế hoạch', icon: 'menu' },
  { to: '/coach/requests', label: 'Yêu cầu', icon: 'send', badge: true },
  { to: '/coach/account', label: 'Tài khoản', icon: 'user' },
];

// Each coach tab's top row: the "Chế độ HLV" chip that opens the mode switch, the tab's own buttons, and the
// coach's bell.
export function CoachTopBar({ children }) {
  const { profile, unreadBySide } = useApp();
  const path = useAppPath();
  const [switching, setSwitching] = useState(false);
  const chipAnchor = useAnnouncementAnchor('coach_mode_chip');
  const unread = unreadBySide?.coach || 0;
  return (
    <div className="pt-safe flex items-center justify-between gap-2 pt-4">
      <button ref={chipAnchor} type="button" onClick={() => setSwitching(true)} aria-label="Chế độ huấn luyện viên, bấm để chuyển chế độ"
        className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#15261f] pl-1 pr-3 text-[13px] font-bold text-white">
        <span className="grid size-7 place-items-center overflow-hidden rounded-full bg-[#6fd3a0] text-[#15261f]">
          <Avatar name={profile?.name} url={profile?.avatar_url} size={28} />
        </span>
        Chế độ HLV
        <Icon name="chevronDown" className="size-4" />
      </button>
      <div className="flex items-center gap-2">
        {children}
        <Link to={path('/coach/notifications')} aria-label={unread > 0 ? `Thông báo, ${unread} chưa đọc` : 'Thông báo'}
          className="relative grid size-11 place-items-center rounded-full border border-line bg-base-100">
          <Icon name="bell" className="size-5" />
          {unread > 0 && <span className="absolute right-2 top-2 size-2.5 rounded-full bg-error ring-2 ring-base-100" />}
        </Link>
      </div>
      <ModeSheet open={switching} current="coach" onClose={() => setSwitching(false)} />
    </div>
  );
}

function CoachBottomBar() {
  const path = useAppPath();
  const { coach } = useApp();
  const count = coach?.requests_count || 0;
  return (
    <nav aria-label="Điều hướng chế độ huấn luyện viên" data-theme="macro-dark"
      className="pb-safe fixed inset-x-0 bottom-0 z-30 bg-base-200 text-base-content">
      <div className="mx-auto grid max-w-xl grid-cols-4">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={path(tab.to)} end={tab.end}
            className={({ isActive }) => `flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs ${isActive ? 'font-bold text-[#6fd3a0]' : 'text-muted'}`}>
            <span className="relative">
              <Icon name={tab.icon} className="size-6" />
              {tab.badge && count > 0 && (
                <span className="absolute -right-2.5 -top-1.5 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-[#e8743b] px-1 text-[11px] font-bold text-white"
                  aria-label={`${count} mục cần xử lý`}>{count > 99 ? '99+' : count}</span>
              )}
            </span>
            {tab.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

// Keeps the Yêu cầu badge and the coach's unread count fresh when the coach comes back to the app.
export function useCoachRefresh() {
  const { api, setCoach, setUnreadBySide } = useApp();
  return useCallback(() => {
    api.get('/coach').then((data) => setCoach((c) => (c ? { ...c, ...data } : c))).catch(() => {});
    api.get('/notifications?audience=coach').then((data) => setUnreadBySide(data.unread_by_side)).catch(() => {});
  }, [api, setCoach, setUnreadBySide]);
}

// Coach mode: its own four tabs on a dark bar, light pages. Without coach mode, the intro page instead.
export default function CoachShell() {
  const { coach } = useApp();
  const path = useAppPath();
  const refresh = useCoachRefresh();
  useRememberMode();
  useEffect(() => {
    const onFocus = () => document.visibilityState === 'visible' && refresh();
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [refresh]);

  if (!coach) return <Navigate to={path('/coach/intro')} replace />;
  return (
    <div data-theme="macro" className="min-h-dvh bg-base-200 text-base-content">
      <LoadingBar />
      <div className="mx-auto flex min-h-dvh max-w-xl flex-col">
        <main className="flex-1 px-4 pb-32"><Outlet /></main>
        <CoachBottomBar />
        <FeedbackButton mode="coach" />
      </div>
    </div>
  );
}
